import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { v1Client } from '@renderer/chat'
import { useGlobalStore } from '@highlight/ipc/react'
import { useMemo, useCallback, useEffect } from 'react'
import { GlobalEvents } from '@highlight/ipc'
import { useDebouncedCalendarSave, type CalendarMetadataChange } from './use-debounced-calendar-save'
import BaseIpcRenderer from '@renderer/ipc/BaseIpc.renderer'
import { useConnections, CONNECTIONS_QUERY_KEY } from './use-connections'
import { isCalendarCapableConnection, isCalendarSyncEligibleConnection } from './calendar-capable-connection'

const CALENDARS_QUERY_KEY = 'calendars'
const SAME_SNAPSHOT_TOLERANCE_MS = 15_000

// Types matching the API response
export interface Calendar {
  id: string
  name: string
  readOnly: boolean
  hexColor: string
  isPrimary: boolean
  timeZone: string
  inviteEmail?: string
  providerName: 'google' | 'microsoft'
  connectionId: string
  // Heterogeneous blob: `enabled` is a boolean, `color`/`order` are strings.
  metadata?: Record<string, string | boolean>
}

interface Connection {
  id: string
  // 'google-calendar' is the virtual sub-provider entry; when it carries
  // `metadata.orgProvided` it is the ONLY entry for org-provided calendar
  // access (there is no real 'google' row to join against).
  providerName: 'google' | 'microsoft' | 'google-calendar'
  providerUserId: string
  email: string
  accessTokenExpiresAt: string
  scopes: Array<string>
  default: boolean
  createdAt: string
  updatedAt: string
  lastAccessAt: string
  // Values are strings except `orgProvided`, an object marker only ever
  // checked for presence here.
  metadata?: Record<string, string>
  hasExpired: boolean
}

// Color options for calendar customization
export const CALENDAR_COLOR_OPTIONS = [
  { label: 'Default', value: 'default', hex: null },
  { label: 'Yellow', value: 'yellow', hex: '#ECFF0C' },
  { label: 'Green', value: 'green', hex: '#75E65D' },
  { label: 'Mint', value: 'mint', hex: '#00C388' },
  { label: 'Cyan', value: 'cyan', hex: '#009A96' },
  { label: 'Teal', value: 'teal', hex: '#007084' },
  { label: 'Blue', value: 'blue', hex: '#2F4858' },
  { label: 'Pink', value: 'pink', hex: '#FF5C7A' },
  { label: 'Purple', value: 'purple', hex: '#9147FF' },
] as const

export type CalendarColorValue = (typeof CALENDAR_COLOR_OPTIONS)[number]['value']

// Generate a deterministic color for calendars without a default color (e.g., Outlook)
export function getDefaultColorForCalendar(calendarId: string): string {
  const colors = CALENDAR_COLOR_OPTIONS.filter((opt) => opt.hex !== null).map((opt) => opt.hex!)

  let hash = 0
  for (let i = 0; i < calendarId.length; i++) {
    hash = ((hash << 5) - hash + calendarId.charCodeAt(i)) | 0
  }

  return colors[Math.abs(hash) % colors.length]
}

// Resolve the effective color for a calendar (handles provider color, custom color, and fallback)
export function getEffectiveCalendarColor(
  calendarId: string,
  hexColor: string | undefined,
  customColor: CalendarColorValue | undefined,
): string {
  const providerColor = hexColor || getDefaultColorForCalendar(calendarId)

  if (!customColor || customColor === 'default') {
    return providerColor
  }

  const colorOption = CALENDAR_COLOR_OPTIONS.find((opt) => opt.value === customColor)
  return colorOption?.hex ?? providerColor
}

// Types for the UI
export interface CalendarAccount {
  accountName: string
  accountEmail: string
  connectionId: string
  isDefault: boolean
  providerName: 'google' | 'microsoft'
  /** Provided by the user's organization — no rename/delete/reorder actions. */
  isOrgProvided: boolean
  order: number
  calendars: Array<{
    id: string
    name: string
    email?: string
    color: string
    customColor?: CalendarColorValue
    enabled: boolean
    readOnly?: boolean
    isPrimary?: boolean
  }>
}

/** A calendar-scoped connection the server marked expired, waiting on a reconnect. */
export interface ExpiredCalendarAccount {
  connectionId: string
  accountEmail: string
  providerName: 'google' | 'microsoft'
}

/** A connected account the server hasn't returned calendars for yet. */
export type PendingCalendarAccount = ExpiredCalendarAccount

export function useCalendars() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()
  const [defaultAccount, setDefaultAccount] = useGlobalStore<string>('settings', 'defaultAccount')

  // Fetch calendars from backend
  const {
    data: calendarsData,
    isLoading: isLoadingCalendars,
    isFetching: isFetchingCalendars,
    isSuccess: isCalendarsSuccess,
    error: calendarsError,
    dataUpdatedAt: calendarsUpdatedAt,
    refetch: refetchCalendars,
  } = useQuery({
    queryKey: [CALENDARS_QUERY_KEY],
    queryFn: async () => {
      const response = await v1Client.GET('/calendars', {
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      })

      if (response.error) {
        throw new Error('Failed to fetch calendars')
      }

      return response.data?.data ?? []
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff: 1s, 2s, 4s
  })

  // Fetch connections to get account information (using shared hook)
  const {
    data: connectionsData,
    isLoading: isLoadingConnections,
    isFetching: isFetchingConnections,
    isSuccess: isConnectionsSuccess,
    error: connectionsError,
    dataUpdatedAt: connectionsUpdatedAt,
    refetch: refetchConnections,
  } = useConnections()

  // `/calendars` and `/connections` are fetched independently and each cache
  // for five minutes, so after a server-side expire or revive one can lag the
  // other. Whichever answered last is the truth about whether an account is
  // live, so the older response never adds a row the newer one contradicts.
  // Responses within a few seconds of each other came from one invalidation
  // and describe the same server state, so neither outranks the other.
  const connectionsAreFresher = connectionsUpdatedAt - calendarsUpdatedAt > SAME_SNAPSHOT_TOLERANCE_MS
  const calendarsAreFresher = calendarsUpdatedAt - connectionsUpdatedAt > SAME_SNAPSHOT_TOLERANCE_MS

  // Note: Connection events are handled globally in use-connections.ts
  // which invalidates connections, calendars, and calendar-events queries

  // Transform API data into UI format grouped by connection
  const calendarAccounts = useMemo<Array<CalendarAccount>>(() => {
    if (!calendarsData || !connectionsData) return []

    const connections = connectionsData as Array<Connection>
    const calendars = calendarsData as Array<Calendar>

    // Group calendars by their owning connection id, then enrich each group
    // from the connections list. Building from calendars (not connections)
    // matters twice over: virtual Google sub-provider rows reuse the real
    // connection's id (iterating connections would render duplicate
    // sections), and org-provided calendar access has no real 'google' row at
    // all — only an orgProvided 'google-calendar' entry.
    const calendarsByConnectionId = new Map<string, Array<Calendar>>()
    for (const cal of calendars) {
      const group = calendarsByConnectionId.get(cal.connectionId)
      if (group) {
        group.push(cal)
      } else {
        calendarsByConnectionId.set(cal.connectionId, [cal])
      }
    }

    const accounts: Array<CalendarAccount> = []

    let index = 0
    for (const [connectionId, connectionCalendars] of calendarsByConnectionId) {
      // Prefer the real calendar-provider entry when several share the id.
      const candidates = connections.filter((c) => c.id === connectionId)
      const connection =
        candidates.find((c) => c.providerName === 'google' || c.providerName === 'microsoft') ?? candidates[0]
      if (connectionsAreFresher && connection?.hasExpired) {
        continue
      }
      // Only a positive server marker makes an account org-provided. A missing
      // connection (transient skew between the two queries) is "unknown", and
      // must degrade to the normal rendering — labeling it org-provided would
      // also hide the rename/delete/reorder actions.
      const isOrgProvided = connection?.metadata?.orgProvided !== undefined

      const accountEmail = connection?.email ?? connectionCalendars[0].inviteEmail ?? ''
      const mappedCalendars = connectionCalendars.map((cal) => ({
        id: cal.id,
        name: cal.name,
        email: accountEmail,
        color: cal.hexColor || getDefaultColorForCalendar(cal.id),
        customColor: (cal.metadata?.color as CalendarColorValue) || undefined,
        enabled: cal.metadata?.enabled === true,
        readOnly: cal.readOnly,
        isPrimary: cal.isPrimary,
      }))

      // Parse order from metadata; fall back to insertion index when absent
      // or non-numeric (NaN never sorts stably).
      const parsedOrder = parseInt(connection?.metadata?.order ?? '', 10)
      const order = Number.isNaN(parsedOrder) ? index : parsedOrder

      accounts.push({
        accountName: accountEmail.split('@')[0] || 'User',
        accountEmail,
        connectionId,
        isDefault: connection?.default ?? false,
        providerName: connectionCalendars[0].providerName,
        isOrgProvided,
        order,
        calendars: mappedCalendars,
      })
      index++
    }

    return accounts
  }, [calendarsData, connectionsData, connectionsAreFresher])

  // Sort accounts by manual order
  const orderedAccounts = useMemo(() => [...calendarAccounts].sort((a, b) => a.order - b.order), [calendarAccounts])

  // Group accounts by provider (kept for backwards compatibility)
  const googleAccounts = useMemo(
    () => calendarAccounts.filter((account) => account.providerName === 'google'),
    [calendarAccounts],
  )

  const outlookAccounts = useMemo(
    () => calendarAccounts.filter((account) => account.providerName === 'microsoft'),
    [calendarAccounts],
  )

  // The server excludes expired rows from `/calendars` entirely (they have no
  // usable calendars to group by), so an expired account has to be read
  // straight off `/connections` instead.
  const expiredAccounts = useMemo<Array<ExpiredCalendarAccount>>(() => {
    if (!connectionsData) {
      return []
    }
    // Calendars in `/calendars` prove the row was live when that response was
    // built, which outranks an expired flag from the same or an older fetch
    // (getCalendars revives inside the request the desktop fetches in parallel).
    const revivedConnectionIds = new Set(
      connectionsAreFresher
        ? []
        : ((calendarsData as Array<Calendar> | undefined) ?? []).map((cal) => cal.connectionId),
    )
    return (connectionsData as Array<Connection>)
      .filter((connection) => connection.hasExpired && !revivedConnectionIds.has(connection.id))
      .filter(isCalendarCapableConnection)
      .map((connection) => ({
        connectionId: connection.id,
        accountEmail: connection.email,
        providerName: connection.providerName as 'google' | 'microsoft',
      }))
  }, [calendarsData, connectionsData, connectionsAreFresher])

  // A live, sync-eligible row with no calendars in `/calendars` is mid-sync:
  // the server leaves it out while its OneCal link is missing or being
  // re-established (right after a reconnect, or while OneCal is rejecting
  // it below the expiry threshold). Without this the account has no row at
  // all in Settings until the sync succeeds or the row expires. A materially
  // older connections response can't vouch for the row still being live (it
  // may have expired since), so it adds nothing.
  const pendingAccounts = useMemo<Array<PendingCalendarAccount>>(() => {
    if (!calendarsData || !connectionsData || calendarsAreFresher) {
      return []
    }
    const connectionIdsWithCalendars = new Set((calendarsData as Array<Calendar>).map((cal) => cal.connectionId))
    return (connectionsData as Array<Connection>)
      .filter((connection) => connection.providerName === 'google' || connection.providerName === 'microsoft')
      .filter((connection) => !connection.hasExpired && !connectionIdsWithCalendars.has(connection.id))
      .filter(isCalendarSyncEligibleConnection)
      .map((connection) => ({
        connectionId: connection.id,
        accountEmail: connection.email,
        providerName: connection.providerName as 'google' | 'microsoft',
      }))
  }, [calendarsData, connectionsData, calendarsAreFresher])

  // Handle setting default account
  const handleSetDefaultAccount = (accountEmail: string) => {
    setDefaultAccount(accountEmail)
    // TODO: If backend supports setting default connection, call API here
    // For now, we're just storing it locally
  }

  // Batch mutation to update multiple calendars at once
  const batchUpdateCalendarsMutation = useMutation({
    mutationFn: async ({ changes }: { changes: Array<CalendarMetadataChange> }) => {
      const requests = changes.map(async (change) => {
        // Send only the changed keys. The server merges them, and resending
        // the cached metadata could carry a stale `enabled` from another
        // window and switch the calendar off.
        const metadata: Record<string, string | boolean> = {}
        if (change.enabled !== undefined) {
          metadata.enabled = change.enabled
        }
        if (change.color !== undefined) {
          metadata.color = change.color
        }

        return v1Client.PATCH('/calendars/{id}', {
          params: { path: { id: change.calendarId } },
          body: { metadata },
          headers: { Authorization: `Bearer ${await getAuthToken()}` },
        })
      })

      const results = await Promise.all(requests)
      const errors = results.filter((r) => r.error)
      if (errors.length > 0) {
        console.error('[useCalendars] Batch PATCH failed:', errors)
        throw new Error('Failed to update some calendars')
      }

      return results
    },
    onMutate: async ({ changes }) => {
      await queryClient.cancelQueries({ queryKey: [CALENDARS_QUERY_KEY] })
      const previousCalendars = queryClient.getQueryData([CALENDARS_QUERY_KEY])

      queryClient.setQueryData([CALENDARS_QUERY_KEY], (old: Array<Calendar> | undefined) => {
        if (!old) return old
        return old.map((cal) => {
          const change = changes.find((c) => c.calendarId === cal.id)
          if (!change) return cal

          return {
            ...cal,
            metadata: {
              ...cal.metadata,
              ...(change.enabled !== undefined && {
                enabled: change.enabled,
              }),
              ...(change.color !== undefined && { color: change.color }),
            },
          }
        })
      })

      return { previousCalendars }
    },
    onError: (error, variables, context) => {
      console.error('[useCalendars] Batch mutation failed:', error)
      if (context?.previousCalendars) {
        queryClient.setQueryData([CALENDARS_QUERY_KEY], context.previousCalendars)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CALENDARS_QUERY_KEY] })
      queryClient.removeQueries({ queryKey: ['calendar-events'] })
      BaseIpcRenderer.refreshCalendarEvents()
    },
  })

  // Debounced save hook for batching calendar changes
  const {
    queueChange,
    isSaving: isSavingCalendars,
    pendingCalendarIds,
    savingCalendarIds,
  } = useDebouncedCalendarSave(async (changes) => {
    await batchUpdateCalendarsMutation.mutateAsync({ changes })
  })

  // Apply optimistic update to React Query cache
  const applyOptimisticUpdate = useCallback(
    (calendarId: string, change: { enabled?: boolean; color?: CalendarColorValue }) => {
      queryClient.setQueryData([CALENDARS_QUERY_KEY], (old: Array<Calendar> | undefined) => {
        if (!old) return old
        return old.map((cal) =>
          cal.id === calendarId
            ? {
                ...cal,
                metadata: {
                  ...cal.metadata,
                  ...(change.enabled !== undefined && {
                    enabled: change.enabled,
                  }),
                  ...(change.color !== undefined && { color: change.color }),
                },
              }
            : cal,
        )
      })
    },
    [queryClient],
  )

  // Toggle calendar enabled state (debounced)
  const toggleCalendar = useCallback(
    async (calendarId: string) => {
      const calendar = (calendarsData as Array<Calendar>)?.find((cal) => cal.id === calendarId)
      const currentlyEnabled = calendar?.metadata?.enabled === true
      const newEnabled = !currentlyEnabled

      // Apply optimistic update immediately
      applyOptimisticUpdate(calendarId, { enabled: newEnabled })

      // Queue debounced save
      await queueChange(calendarId, { enabled: newEnabled })
    },
    [calendarsData, applyOptimisticUpdate, queueChange],
  )

  // Set calendar color (debounced)
  const setCalendarColor = useCallback(
    async (calendarId: string, color: CalendarColorValue) => {
      // Apply optimistic update immediately
      applyOptimisticUpdate(calendarId, { color })

      // Queue debounced save
      await queueChange(calendarId, { color })
    },
    [applyOptimisticUpdate, queueChange],
  )

  // Mutation to update connection metadata
  const updateConnectionMetadataMutation = useMutation({
    mutationFn: async ({ connectionId, metadata }: { connectionId: string; metadata: Record<string, string> }) => {
      const response = await v1Client.PATCH('/connections/{id}', {
        params: {
          path: { id: connectionId },
        },
        body: {
          metadata,
        },
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      })

      if (response.error) {
        throw new Error('Failed to update connection')
      }

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONNECTIONS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [CALENDARS_QUERY_KEY] })
    },
  })

  // Mutation to delete a connection
  const deleteConnectionMutation = useMutation({
    mutationFn: async ({ connectionId }: { connectionId: string }) => {
      const response = await v1Client.DELETE('/connections/{id}', {
        params: {
          path: { id: connectionId },
        },
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      })

      if (response.error) {
        throw new Error('Failed to delete connection')
      }

      return response.data
    },
    onSuccess: (data, variables) => {
      // Find the provider type of the deleted connection
      const connection = (connectionsData as Array<Connection>)?.find((c) => c.id === variables.connectionId)

      // Emit disconnect event based on provider
      if (connection?.providerName === 'google') {
        GlobalEvents.emit('connections.google.disconnect')
      } else if (connection?.providerName === 'microsoft') {
        GlobalEvents.emit('connections.microsoft.disconnect')
      }

      queryClient.invalidateQueries({ queryKey: [CONNECTIONS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [CALENDARS_QUERY_KEY] })
    },
  })

  const refetch = () => {
    refetchCalendars()
    refetchConnections()
  }

  // Existing metadata to resend alongside an `order` write. `orgProvided` is a
  // server-controlled namespace — echoing it back makes the PATCH 400, and a
  // swap with an org-provided neighbor would otherwise do exactly that. The
  // server preserves it on the row regardless (jsonb concat), so dropping it
  // from the payload loses nothing.
  const reorderableMetadata = (connection: Connection | undefined): Record<string, string> => {
    const { orgProvided: _orgProvided, ...rest } = connection?.metadata ?? {}
    return rest
  }

  // Move account up in the order
  const moveAccountUp = useCallback(
    async (connectionId: string) => {
      const currentIndex = orderedAccounts.findIndex((a) => a.connectionId === connectionId)
      if (currentIndex <= 0) return // Already at top

      const currentAccount = orderedAccounts[currentIndex]
      const aboveAccount = orderedAccounts[currentIndex - 1]

      // Get existing metadata
      const currentConnection = (connectionsData as Array<Connection>)?.find(
        (c) => c.id === currentAccount.connectionId,
      )
      const aboveConnection = (connectionsData as Array<Connection>)?.find((c) => c.id === aboveAccount.connectionId)

      // Swap order values
      const newCurrentOrder = aboveAccount.order
      const newAboveOrder = currentAccount.order

      // Update both connections
      await Promise.all([
        updateConnectionMetadataMutation.mutateAsync({
          connectionId: currentAccount.connectionId,
          metadata: { ...reorderableMetadata(currentConnection), order: String(newCurrentOrder) },
        }),
        updateConnectionMetadataMutation.mutateAsync({
          connectionId: aboveAccount.connectionId,
          metadata: { ...reorderableMetadata(aboveConnection), order: String(newAboveOrder) },
        }),
      ])
    },
    [orderedAccounts, connectionsData, updateConnectionMetadataMutation],
  )

  // Move account down in the order
  const moveAccountDown = useCallback(
    async (connectionId: string) => {
      const currentIndex = orderedAccounts.findIndex((a) => a.connectionId === connectionId)
      if (currentIndex < 0 || currentIndex >= orderedAccounts.length - 1) return // Already at bottom

      const currentAccount = orderedAccounts[currentIndex]
      const belowAccount = orderedAccounts[currentIndex + 1]

      // Get existing metadata
      const currentConnection = (connectionsData as Array<Connection>)?.find(
        (c) => c.id === currentAccount.connectionId,
      )
      const belowConnection = (connectionsData as Array<Connection>)?.find((c) => c.id === belowAccount.connectionId)

      // Swap order values
      const newCurrentOrder = belowAccount.order
      const newBelowOrder = currentAccount.order

      // Update both connections
      await Promise.all([
        updateConnectionMetadataMutation.mutateAsync({
          connectionId: currentAccount.connectionId,
          metadata: { ...reorderableMetadata(currentConnection), order: String(newCurrentOrder) },
        }),
        updateConnectionMetadataMutation.mutateAsync({
          connectionId: belowAccount.connectionId,
          metadata: { ...reorderableMetadata(belowConnection), order: String(newBelowOrder) },
        }),
      ])
    },
    [orderedAccounts, connectionsData, updateConnectionMetadataMutation],
  )

  // Compute enabled calendar IDs from backend metadata
  const enabledCalendarIds = useMemo(() => {
    if (!calendarsData) return []
    const calendars = calendarsData as Array<Calendar>
    return calendars.filter((cal) => cal.metadata?.enabled === true).map((cal) => cal.id)
  }, [calendarsData])

  return {
    // Grouped accounts
    googleAccounts,
    outlookAccounts,
    allAccounts: calendarAccounts,
    orderedAccounts,
    expiredAccounts,
    pendingAccounts,

    // Loading states
    isLoading: isLoadingCalendars || isLoadingConnections,
    isFetching: isFetchingCalendars || isFetchingConnections,
    // True only once both underlying queries have succeeded. Until then an
    // empty account list doesn't mean "no calendars".
    isSuccess: isCalendarsSuccess && isConnectionsSuccess,
    // Debounced save states
    isSavingCalendars,
    pendingCalendarIds,
    savingCalendarIds,

    // Error states
    error: calendarsError?.message || connectionsError?.message,
    // True only when the query errored with nothing cached to fall back on.
    // A background refetch failure alongside still-usable data doesn't count.
    hasCalendarsLoadError: Boolean(calendarsError) && calendarsData === undefined,
    hasConnectionsLoadError: Boolean(connectionsError) && connectionsData === undefined,

    // Actions
    refetch,
    setDefaultAccount: handleSetDefaultAccount,
    toggleCalendar,
    setCalendarColor,
    defaultAccount,
    enabledCalendarIds,
    updateConnectionMetadata: updateConnectionMetadataMutation.mutateAsync,
    deleteConnection: deleteConnectionMutation.mutateAsync,
    isUpdatingConnection: updateConnectionMetadataMutation.isPending,
    isDeletingConnection: deleteConnectionMutation.isPending,
    moveAccountUp,
    moveAccountDown,

    // Raw data (if needed). The query data is loosely typed by the API client;
    // this hook already treats it as Array<Calendar> internally, so the cast
    // lives here — the single boundary that owns the shape — rather than at
    // every consumer.
    calendars: calendarsData as Array<Calendar> | undefined,
    connections: connectionsData,
  }
}

/** Returns the resolved color and display name for a calendar event's calendar. */
export function useCalendarColor(calendarId: string | undefined): {
  calendarColor: string | undefined
  calendarName: string | undefined
} {
  const { calendars } = useCalendars()
  return useMemo(() => {
    if (!calendarId || !calendars) return { calendarColor: undefined, calendarName: undefined }
    const cal = calendars.find((c) => c.id === calendarId)
    if (!cal) return { calendarColor: undefined, calendarName: undefined }
    return {
      calendarColor: getEffectiveCalendarColor(
        cal.id,
        cal.hexColor,
        cal.metadata?.color as CalendarColorValue | undefined,
      ),
      calendarName: cal.name,
    }
  }, [calendarId, calendars])
}
